import { inject, Injectable } from '@angular/core';
import { ActiveUserStore } from '../stores/user/active-user.state';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToolAccess, UserLevel } from '../models/user';
import { IndicatorStore } from '../stores/indicator/indicator.state';
import { CategoryStore } from '../stores/category/category.state';
import { PermissionStore } from '../stores/permission.state';
import { Access } from '../models/rbac';
import { BookmarkCategoryStore } from '../stores/bookmark-category/bookmark-category.state';
import { DataLayerTypeStore } from '../stores/data-layer-type/data-layer-type.state';
import { TopicStore } from '../stores/topic/topic.state';
import { RoleStore } from '../stores/role/role.state';
import { WorkspaceStore } from '../stores/workspace/workspace.state';
import { WorkspaceChildren, WorkspaceTypeKey } from '../models/workspace';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  readonly workspaceStore = inject(WorkspaceStore);
  readonly permissionStore = inject(PermissionStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);
  readonly bookmarkCategoryStore = inject(BookmarkCategoryStore);
  readonly dataLayerTypeStore = inject(DataLayerTypeStore);
  readonly topicStore = inject(TopicStore);

  constructor(
    private router: Router,
    private messageService: MessageService
  ) {}

  canActivate(routePermission: keyof ToolAccess, redirect = true): boolean {
    let hasPermission = false;

    if (this.adminBypass()) return true;

    switch (routePermission) {
      case 'entityAdder':
        hasPermission = !!this.activeUserStore.canAccessEntityAdder();
        break;
      case 'userManagement':
        hasPermission = !!this.activeUserStore.canAccessUserManagement();
        break;
      case 'config':
        hasPermission = !!this.activeUserStore.canAccessConfig();
        break;
      case 'indicators':
        hasPermission = !!this.activeUserStore.canAccessIndicators();
        break;
      case 'pool':
        hasPermission = !!this.activeUserStore.canAccessPool();
        break;
      case 'themes':
        hasPermission = !!this.activeUserStore.canAccessTopics();
        break;
      case 'bookmarks':
        hasPermission = !!this.activeUserStore.canAccessBookmarks();
        break;
      case 'stories':
        hasPermission = !!this.activeUserStore.canAccessStories();
        break;
      case 'colorsets':
        hasPermission = !!this.activeUserStore.canAccessColorsets();
        break;
      case 'importExport':
        hasPermission = !!this.activeUserStore.canAccessImportExport();
        break;
      case 'massActions':
        hasPermission = !!this.activeUserStore.canAccessMassActions();
        break;
      case 'history':
        hasPermission = !!this.activeUserStore.canAccessHistory();
        break;
      default:
        return false;
    }

    if (!hasPermission && redirect) {
      this.blockPermission();
      return false;
    }

    return hasPermission;
  }

  /**
   * Retrieves the children elements of a workspace based on the provided workspace ID.
   *
   * @param {string} workspaceId - The unique identifier of the workspace for which children elements are retrieved.
   * @return {WorkspaceChildren} An object containing filtered children elements of the workspace, such as indicator categories, topics, bookmark categories, data layer types, and POI data layer types.
   */
  getWorkspaceChildren(workspaceId: string): WorkspaceChildren {
    return {
      indicatorCategory: this.categoryStore.filterByWorkspace(workspaceId),
      topic: this.topicStore.filterByWorkspace(workspaceId),
      bookmarkCategory:
        this.bookmarkCategoryStore.filterByWorkspace(workspaceId),
      dataLayerType: this.dataLayerTypeStore.filterByWorkspace(
        workspaceId,
        'Polygon'
      ),
      poiDataLayerType: this.dataLayerTypeStore.filterByWorkspace(
        workspaceId,
        'Point'
      ),
    };
  }

  userWorkspaceIdsByType(type: WorkspaceTypeKey): string[] {
    return this.permissionStore.userWorkspaceIdsByType(type);
  }

  /**
   * Restricts access by displaying an appropriate warning message to the user and redirects them to a specified route.
   *
   * @return {void} Does not return a value.
   */
  blockPermission(): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Zugriff verweigert',
      detail:
        'Wenden Sie sich an Ihre Administrator:in um Zugriff auf die URL zu erhalten',
    });
    this.router.navigate(['/data-backend']);
  }

  /**
   * check indicator or category rights recursively
   * @param categoryId
   * @param actions
   * @param redirect
   */
  checkIndicatorRights(
    categoryId: string,
    actions: keyof Access | Array<keyof Access>,
    redirect = false
  ): boolean {
    if (this.adminBypass()) return true;
    const workspaceIds =
      this.categoryStore.getAllWorkspacesFromPath(categoryId);

    return this.handleEntityAccess(
      'indicatorCategory',
      categoryId,
      workspaceIds,
      actions,
      redirect
    );
  }

  /**
   * check bookmark or bookmark-category rights recursively
   * @param categoryId
   * @param actions
   * @param redirect
   */
  checkBookmarkRights(
    categoryId: string,
    actions: keyof Access | Array<keyof Access>,
    redirect = false
  ): boolean {
    if (this.adminBypass()) return true;
    const workspaceIds =
      this.bookmarkCategoryStore.getById(categoryId)?.workspaces || [];

    return this.handleEntityAccess(
      'bookmarkCategory',
      categoryId,
      workspaceIds,
      actions,
      redirect
    );
  }

  /**
   * Checks the rights to access a specific data layer based on the provided type ID and actions.
   *
   * @param {string} typeId - The identifier of the data layer type.
   * @param {keyof Access | Array<keyof Access>} actions - The required action or list of actions to check access for.
   * @param {boolean} [redirect=false] - Optional flag to determine whether to execute a redirect if access is denied.
   * @return {boolean} - Returns true if the access rights are sufficient, otherwise false.
   */
  checkDataLayerRights(
    typeId: string,
    actions: keyof Access | Array<keyof Access>,
    redirect = false
  ): boolean {
    if (this.adminBypass()) return true;
    const item = this.dataLayerTypeStore.getById(typeId);
    if (!item) return false;

    const workspaceType: WorkspaceTypeKey =
      item?.type === 'Polygon' ? 'dataLayerType' : 'poiDataLayerType';
    const workspaceIds = item.workspaces || [];

    return this.handleEntityAccess(
      workspaceType,
      typeId,
      workspaceIds,
      actions,
      redirect
    );
  }

  /**
   * Checks if the user has the necessary rights to perform specified actions on a theme.
   *
   * @param {string} id - The unique identifier of the theme entity to check rights for.
   * @param {keyof Access | Array<keyof Access>} actions - The action or list of actions to verify access permissions.
   * @param {boolean} [redirect=false] - Indicates whether to redirect the user if access is denied.
   * @return {boolean} Returns true if the user has the required rights, otherwise false.
   */
  checkThemeRights(
    id: string,
    actions: keyof Access | Array<keyof Access>,
    redirect = false
  ): boolean {
    if (this.adminBypass()) return true;
    const workspaceIds = this.topicStore.getById(id)?.workspaces || [];

    return this.handleEntityAccess(
      'topic',
      id,
      workspaceIds,
      actions,
      redirect
    );
  }

  /**
   * Handles access permissions for specific entities based on workspace IDs and actions.
   *
   * @param {string[]} workspaceIds - An array of workspace IDs to check permissions for.
   * @param {keyof Access | Array<keyof Access>} actions - The action or actions being checked for permission.
   * @param {boolean} [redirect=false] - Indicates whether to redirect if access is denied.
   * @return {boolean} - Returns true if access is granted, otherwise false.
   */
  handleEntityAccess(
    type: WorkspaceTypeKey,
    entityId: string,
    workspaceIds: string[],
    actions: keyof Access | Array<keyof Access>,
    redirect = false
  ): boolean {
    if (this.adminBypass(10)) return true;

    const hasAccess = this.permissionStore.hasPermissionForEntity(
      type,
      entityId,
      actions,
      workspaceIds
    );

    if (!hasAccess && redirect) {
      console.log(
        'blocked by handleEntityAccess in permission.service.ts:',
        type,
        entityId,
        workspaceIds,
        actions
      );
      this.blockPermission();
    }
    return hasAccess;
  }

  /**
   * Checks if the current active user has administrative privileges or a user level greater than 0.
   *
   * @return {boolean} Returns true if the active user is an admin or has a user level greater than 0, otherwise false.
   */
  adminBypass(level: UserLevel = 10): boolean {
    const user = this.activeUserStore.user();
    return !!user && (user.isAdmin || user.level > level);
  }

  /**
   * Determines if the user has 'add' rights for the specified entity type
   * based on the workspace type.
   *
   * @param {WorkspaceTypeKey} workspaceType - The key representing the type of workspace.
   * @return {boolean} True if the user has 'add' rights for the specified entity type, otherwise false.
   */
  hasAddRightsByEntityType(workspaceType: WorkspaceTypeKey): boolean {
    return this.hasActionRightForEntityType(workspaceType, 'add');
  }

  /**
   * Determines if the user has 'edit' rights for the specified entity type
   * based on the workspace type.
   *
   * @param {WorkspaceTypeKey} workspaceType - The key representing the type of workspace.
   * @return {boolean} True if the user has 'add' rights for the specified entity type, otherwise false.
   */
  hasEditRightsByEntityType(workspaceType: WorkspaceTypeKey): boolean {
    return this.hasActionRightForEntityType(workspaceType, 'edit');
  }

  /**
   * Determines if the user has 'remove' rights for the specified entity type
   * based on the workspace type.
   *
   * @param {WorkspaceTypeKey} workspaceType - The key representing the type of workspace.
   * @return {boolean} True if the user has 'add' rights for the specified entity type, otherwise false.
   */
  hasRemoveRightsByEntityType(workspaceType: WorkspaceTypeKey): boolean {
    return this.hasActionRightForEntityType(workspaceType, 'remove');
  }

  /**
   * Checks if the user can add anything (any entity type in any workspace)
   *
   * @return {boolean} True if the user has add rights for at least one entity type
   */
  canAddAnything(): boolean {
    if (this.adminBypass(10)) return true;
    return this.permissionStore.canAddAnything();
  }

  hasActionRightForEntityType(
    workspaceType: WorkspaceTypeKey,
    action: keyof Access
  ): boolean {
    if (this.adminBypass(10)) return true;
    return this.permissionStore.hasPermissionForType(workspaceType, action);
  }

  /**
   *
   */
  canImportIndicators(): boolean {
    return this.hasActionRightForEntityType('indicatorCategory', 'import');
  }

  canExportIndicators() {
    return this.hasActionRightForEntityType('indicatorCategory', 'export');
  }

  canImportData() {
    return (
      this.hasActionRightForEntityType('indicatorCategory', 'import') &&
      (this.hasActionRightForEntityType('dataLayerType', 'import') ||
        this.hasActionRightForEntityType('poiDataLayerType', 'export'))
    );
  }

  canExportData() {
    return (
      this.hasActionRightForEntityType('indicatorCategory', 'export') &&
      (this.hasActionRightForEntityType('dataLayerType', 'export') ||
        this.hasActionRightForEntityType('poiDataLayerType', 'import'))
    );
  }

  canImportGeoData() {
    return this.hasActionRightForEntityType('dataLayerType', 'import');
  }

  canExportGeoData() {
    return this.hasActionRightForEntityType('dataLayerType', 'export');
  }

  canImportPoi() {
    return this.hasActionRightForEntityType('poiDataLayerType', 'import');
  }

  canExportPoi() {
    return this.hasActionRightForEntityType('poiDataLayerType', 'export');
  }
}
